import {
  Autocomplete,
  FormControl,
  type FormControlProps,
  FormHelperText,
  LinearProgress,
  TextField,
} from "@mui/material";
import { type FC, useMemo } from "react";
import type { UUID } from "../../../../utils/uuid";
import type { ProgramsState } from "../../hooks/use-programs";

export type ProgramSelectProps = Omit<FormControlProps, "onChange"> & {
  value?: UUID;
  onChange: (value: UUID) => void;
  errorMessage?: string;
  programs: ProgramsState;
};

/** Select showing `program`s. */
export const ProgramSelect: FC<ProgramSelectProps> = ({
  value,
  onChange,
  errorMessage,
  disabled,
  programs,
  ...props
}) => {
  const usedPrograms = useMemo(() => {
    if (!programs.data) return [];

    if (disabled) return programs.data.viewable;

    return programs.data.editable;
  }, [programs.data, disabled]);

  const selectedValue = useMemo(
    () => usedPrograms?.find((program) => program.id === value) || null,
    [value, usedPrograms],
  );

  if (programs.loading) return <LinearProgress />;

  return (
    <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }} {...props} fullWidth>
      <Autocomplete
        disabled={disabled}
        value={selectedValue}
        options={usedPrograms}
        renderInput={(params) => (
          <TextField
            variant="standard"
            label="Program"
            error={Boolean(errorMessage)}
            {...params}
            slotProps={{
              input: {
                ...params.InputProps,
                "data-testid": "program-select-input",
              },
            }}
          />
        )}
        onChange={(event, v) => {
          onChange(v?.id as UUID);
        }}
        getOptionLabel={(option) => option.title}
      />
      {errorMessage && (
        <FormHelperText error variant="filled">
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
};
